// File: src/components/pages/InstructorBookingSlots.jsx
// ServiceProvider view - Displays instructor's own published booking slots
// Same UI/logic as BookYourSlots.jsx but without booking functionality

import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { ChevronLeft, ChevronRight, Clock, Coffee, AlertCircle, Sparkles } from 'lucide-react';
import { bookingSlotAPI, bookingAPI } from '../../services/api';
import { getMondayOfCurrentWeek } from '../../utils/DateUtils';
import { AUTH0_NAMESPACE, DAYS_OF_WEEK } from '../../utils/constants';
import '../../styles/unified.css';

export default function InstructorBookingSlots() {
  const { user, getAccessTokenSilently } = useAuth0();
  
  const userRoles = user?.[`${AUTH0_NAMESPACE}/roles`] || [];
  const isServiceProvider = userRoles.includes('ServiceProvider');
  const userAuth0Id = user?.sub;

  const [publishedTemplate, setPublishedTemplate] = useState(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(getMondayOfCurrentWeek());
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [existingBookings, setExistingBookings] = useState(new Set());
  const [bookedSlotNameMap, setBookedSlotNameMap] = useState(new Map());  

  useEffect(() => {
    if (isServiceProvider && userAuth0Id) {
      loadInstructorPublishedTemplate();
    }
  }, [isServiceProvider, userAuth0Id]);

  const loadInstructorPublishedTemplate = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await bookingSlotAPI.getPublishedTemplate(getAccessTokenSilently);
      
      if (response.success && response.data) {
        setPublishedTemplate(response.data);
        
        // Load existing bookings for this instructor
        await loadExistingBookings(response.data.spPhoneId);
      } else {
        setPublishedTemplate(null);
        setError('No published template found. Please create and publish a slot template first.');
      }
    } catch (err) {
      console.error('Failed to load published template:', err);
      setPublishedTemplate(null);
      setError('No published template found. Please create and publish a slot template first.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Load existing bookings for the instructor to show booked slots
   */
  const loadExistingBookings = async (instructorPhone) => {
    try {
      console.log('Loading existing bookings for instructor:', instructorPhone);
      
      const response = await bookingAPI.getBookingsByInstructorPhone(
        instructorPhone,
        getAccessTokenSilently
      );
      if (response.success && response.data) {
        // Create a Set of booked slot keys (date-startTime-endTime)
        const bookedSlots = new Set();

         // Map to track slotKey -> studentName
        const slotNameMap = new Map();

        console.log('Existing bookings response:', response.data);
        response.data.forEach(booking => {
          // Only include active bookings (not cancelled)
          if (booking.status !== 'cancelled') {
            const slotKey = `${booking.date}-${booking.startTime}-${booking.endTime}`;
            bookedSlots.add(slotKey);
            slotNameMap.set(slotKey, booking.studentName);
                      }
        });
        
        console.log(`Found ${bookedSlots.size} existing bookings`);
        setExistingBookings(bookedSlots);
        setBookedSlotNameMap(slotNameMap);
      }
    } catch (err) {
      console.error('Failed to load existing bookings:', err);
      // Don't block the UI if this fails - just log the error
    }
  };

  const navigateWeek = (direction) => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(currentWeekStart.getDate() + (direction * 7));
    setCurrentWeekStart(newStart);
  };

  // Helper functions (same as BookYourSlots.jsx)
  const getWeekDates = (startDate) => {
    const dates = [];
    const start = new Date(startDate);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  const isDateInRange = (date) => {
    if (!publishedTemplate) return false;
    const checkDate = new Date(date);
    const from = new Date(publishedTemplate.settings.dateFrom + 'T00:00:00');
    const to   = new Date(publishedTemplate.settings.dateTo   + 'T00:00:00');
    return checkDate >= from && checkDate <= to;
  };

  const isDayEnabled = (date) => {
    if (!publishedTemplate) return false;
    const dayName = DAYS_OF_WEEK[date.getDay()];
    const fromIndex = DAYS_OF_WEEK.indexOf(publishedTemplate.settings.dayFrom);
    const toIndex = DAYS_OF_WEEK.indexOf(publishedTemplate.settings.dayTo);
    const dayIndex = DAYS_OF_WEEK.indexOf(dayName);
    
    if (fromIndex <= toIndex) {
      return dayIndex >= fromIndex && dayIndex <= toIndex;
    } else {
      return dayIndex >= fromIndex || dayIndex <= toIndex;
    }
  };

  const isDateBookable = (date) => {
    if (!publishedTemplate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    const advanceDate = new Date(today);
    advanceDate.setDate(today.getDate() + publishedTemplate.settings.daysInAdvance);
    return checkDate >= today && checkDate <= advanceDate;
  };

  const timeToMinutes = (timeStr) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // FIXED: toISOString() is UTC — during BST midnight local = 23:00 UTC previous day
  const toLocalDateStr = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;


  const getExceptionalEntry = (date) => {
    if (!publishedTemplate) return null;
    const dateStr = toLocalDateStr(date);
    return (publishedTemplate.exceptionalSlots || []).find(es => es.date === dateStr) || null;
  };

  const isSlotBlocked = (date, slotStart, slotEnd) => {
    if (!publishedTemplate) return { blocked: false };
    const slotStartMin = timeToMinutes(slotStart);
    const slotEndMin = timeToMinutes(slotEnd);
    
    const dateStr = toLocalDateStr(date);
    const privateAppointments = publishedTemplate.privateAppointments || [];
    
    for (const apt of privateAppointments) {
      if (apt.date === dateStr) {
        const aptStartMin = timeToMinutes(apt.timeFrom);
        const aptEndMin = timeToMinutes(apt.timeTo);
        if (!(slotEndMin <= aptStartMin || slotStartMin >= aptEndMin)) {
          return { blocked: true, reason: 'Private' };
        }
      }
    }
    
    return { blocked: false };
  };

  /**
   * Check if a slot is already booked by a student
   */
  const isSlotBooked = (date, slotStart, slotEnd) => {
    const dateStr = toLocalDateStr(date);
    const slotKey = `${dateStr}-${slotStart}-${slotEnd}`;
    return existingBookings.has(slotKey);
  };

  const generateTimeSlots = () => {
    if (!publishedTemplate) return [];
    
    const slots = [];
    const settings = publishedTemplate.settings;
    const [startHour, startMin] = settings.startTime.split(':').map(Number);
    const [endHour, endMin] = settings.endTime.split(':').map(Number);
    
    const startMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;
    const slotDuration = settings.slotDuration;
    const breakDuration = settings.breakDuration;
    
    const hasMyBreak = settings.myBreakFrom && settings.myBreakTo;
    const myBreakStartMin = hasMyBreak ? timeToMinutes(settings.myBreakFrom) : null;
    const myBreakEndMin = hasMyBreak ? timeToMinutes(settings.myBreakTo) : null;
    
    let currentMinutes = startMinutes;
    let breakInserted = false;
    
    while (currentMinutes + slotDuration <= endMinutes) {
      if (hasMyBreak && !breakInserted && currentMinutes + slotDuration > myBreakStartMin) {
        if (currentMinutes < myBreakStartMin) {
          const slotStart = `${Math.floor(currentMinutes / 60).toString().padStart(2, '0')}:${(currentMinutes % 60).toString().padStart(2, '0')}`;
          const slotEnd = `${Math.floor(myBreakStartMin / 60).toString().padStart(2, '0')}:${(myBreakStartMin % 60).toString().padStart(2, '0')}`;
          
          if (myBreakStartMin - currentMinutes >= slotDuration) {
            slots.push({ start: slotStart, end: slotEnd, type: 'slot' });
          }
        }
        
        const breakStart = `${Math.floor(myBreakStartMin / 60).toString().padStart(2, '0')}:${(myBreakStartMin % 60).toString().padStart(2, '0')}`;
        const breakEnd = `${Math.floor(myBreakEndMin / 60).toString().padStart(2, '0')}:${(myBreakEndMin % 60).toString().padStart(2, '0')}`;
        slots.push({ start: breakStart, end: breakEnd, type: 'break' });
        
        currentMinutes = myBreakEndMin;
        breakInserted = true;
        continue;
      }
      
      const slotStart = `${Math.floor(currentMinutes / 60).toString().padStart(2, '0')}:${(currentMinutes % 60).toString().padStart(2, '0')}`;
      const slotEndMinutes = currentMinutes + slotDuration;
      const slotEnd = `${Math.floor(slotEndMinutes / 60).toString().padStart(2, '0')}:${(slotEndMinutes % 60).toString().padStart(2, '0')}`;
      
      slots.push({ start: slotStart, end: slotEnd, type: 'slot' });
      currentMinutes = slotEndMinutes + breakDuration;
    }
    
    return slots;
  };

  const countWindowSlots = (windows) => (windows || []).length;

  const isWindowBooked = (date, win) => {
    const key = `${toLocalDateStr(date)}-${win.startTime}-${win.endTime}`;
    return existingBookings.has(key);
  };

  const fmtExcDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatTime12hr = (time24) => {
    const [hour, min] = time24.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${min.toString().padStart(2, '0')} ${period}`;
  };

  const weekDates = getWeekDates(currentWeekStart);
  const timeSlots = generateTimeSlots();

  if (!isServiceProvider) {
    return (
      <div className="tab-content-placeholder">
        <h2>Access Denied</h2>
        <p>This page is only accessible to instructors</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="booking-container">
        <div className="loading-state">
          <Clock size={48} className="loading-icon" />
          <h2>Loading your booking slots...</h2>
        </div>
      </div>
    );
  }

  if (error && !publishedTemplate) {
    return (
      <div className="booking-container">
        <div className="error-state">
          <AlertCircle size={64} className="error-icon" />
          <h2>No Published Template</h2>
          <p className="error-message">{error}</p>
          <p className="error-hint">Go to Manage Configurations → for to Templates → 
            Update and Save a slot template  → Go to Publish → Submit</p>
        </div>
      </div>
    );
  }

  if (!publishedTemplate) {
    return (
      <div className="booking-container">
        <div className="error-state">
          <AlertCircle size={64} className="error-icon" />
          <h2>No Schedule Available</h2>
          <p className="error-message">Please create and publish a slot template first.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-container">
      {/* Slots Grid - Exact same structure as BookYourSlots.jsx */}
      <div className="slots-preview-card">
        <div className="preview-header">
          <h2 className="preview-title">Your Published Schedule</h2>
          <div className="week-navigation">
            <button onClick={() => navigateWeek(-1)} className="nav-button">
              <ChevronLeft size={24} />
            </button>
            <span className="week-label">
              {weekDates[0].toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} - {weekDates[6].toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
            <button onClick={() => navigateWeek(1)} className="nav-button">
              <ChevronRight size={24} />
            </button>
          </div>
        </div>

        <div className="grid-container">
          <div className="grid-wrapper">
            <div className="grid-header">
              <div className="time-header">Time</div>
              {weekDates.map((date, idx) => {
                const excEntry    = getExceptionalEntry(date);
                const isExc       = !!excEntry;
                const isInRange   = isDateInRange(date);
                const isDayActive = isDayEnabled(date);
                const isBookable  = isDateBookable(date);
                const slotCount = isExc ? (excEntry.windows || []).length : 0;

                if (isExc) {
                  return (
                    <div key={idx} className="day-header day-header-exceptional">
                      <div className="day-name">{DAYS_OF_WEEK[date.getDay()].slice(0, 3)}</div>
                      <div className="day-date day-date-exceptional">{date.getDate()}</div>
                      <div className="day-status day-status-exceptional">
                        <Sparkles size={10} /> Exc. ({slotCount})
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={idx} className={`day-header ${isInRange && isDayActive ? 'day-header-active' : 'day-header-inactive'}`}>
                    <div className="day-name">{DAYS_OF_WEEK[date.getDay()].slice(0, 3)}</div>
                    <div className={`day-date ${isInRange && isDayActive ? 'day-date-active' : 'day-date-inactive'}`}>{date.getDate()}</div>
                    {isInRange && isDayActive && (
                      <div className={`day-status ${isBookable ? 'day-status-open' : 'day-status-future'}`}>
                        {isBookable ? '✓ Open' : 'Future'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="slots-container">
              {timeSlots.map((slot, slotIdx) => (
                <div key={slotIdx} className={`slot-row ${slot.type === 'break' ? 'break-row' : ''}`}>
                  <div className="time-cell">
                    <Coffee size={14} className={slot.type === 'break' ? 'break-icon' : 'time-icon'} />
                    <div className="time-range">
                      <div className="time-start">{formatTime12hr(slot.start)}</div>
                      <div className="time-end">{formatTime12hr(slot.end)}</div>
                    </div>
                  </div>
                  {slot.type === 'break' ? (
                    <div className="break-cell-full" style={{ gridColumn: '2 / -1' }}>
                      <div className="break-label">
                        <Coffee size={16} />
                        <span>BREAK TIME</span>
                      </div>
                    </div>
                  ) : (
                    weekDates.map((date, dateIdx) => {
                      const excEntry    = getExceptionalEntry(date);
                      const isExc       = !!excEntry;
                      const isInRange   = isDateInRange(date);
                      const isDayActive = isDayEnabled(date);
                      const isBookable  = isDateBookable(date);
                      const blockCheck  = isSlotBlocked(date, slot.start, slot.end);
                      const isBooked    = isSlotBooked(date, slot.start, slot.end);

                      // ── Exceptional date — all cells display as exceptional ──
                      if (isExc) {
                        return (
                          <div key={dateIdx} className="slot-cell slot-exceptional">
                            <div className="slot-status">
                              <div className="status-exceptional"><Sparkles size={10} /> Exc.</div>
                            </div>
                          </div>
                        );
                      }

                      // ── Regular date ──────────────────────────────────────
                      const isAvailable = isInRange && isDayActive && isBookable;
                      return (
                        <div
                          key={dateIdx}
                          className={`slot-cell ${
                            !isAvailable        ? 'slot-unavailable' :
                            blockCheck.blocked  ? 'slot-blocked'     :
                            isBooked            ? 'slot-booked'      :
                            'slot-available'
                          }`}
                        >
                          <div className="slot-status">
                            {!isAvailable ? (
                              <div className="status-unavailable">-</div>
                            ) : blockCheck.blocked ? (
                              <div className="status-blocked">{blockCheck.reason}</div>
                            ) : isBooked ? (
                              <div className="status-booked">
                                {bookedSlotNameMap.get(`${toLocalDateStr(date)}-${slot.start}-${slot.end}`)} Booked<br />
                                From {formatTime12hr(slot.start)}
                              </div>
                            ) : (
                              <div className="status-available">
                                {formatTime12hr(slot.start)}<br />{formatTime12hr(slot.end)}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              ))}
            </div>

            {timeSlots.length === 0 && (
              <div className="empty-state">
                <Clock size={48} className="empty-icon" />
                <p className="empty-text">No time slots available</p>
              </div>
            )}
          </div>
        </div>
        <div className="legend">
          <div className="legend-item">
            <div className="legend-box legend-box-available"></div>
            <span className="legend-text">Available for booking</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-booked"></div>
            <span className="legend-text">Booked by student</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-blocked"></div>
            <span className="legend-text">Blocked (Break/Private)</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-locked"></div>
            <span className="legend-text">Future (not yet bookable)</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-unavailable"></div>
            <span className="legend-text">Outside range/unavailable</span>
          </div>
          <div className="legend-item">
            <div className="legend-box legend-box-exceptional"></div>
            <span className="legend-text">Exceptional slot</span>
          </div>
        </div>

        {/* Exceptional Slots — weekly summary */}
        {(() => {
          const weekDateStrs = new Set(weekDates.map(d => toLocalDateStr(d)));
          const weekExcSlots = (publishedTemplate?.exceptionalSlots || [])
            .filter(es => weekDateStrs.has(es.date))
            .sort((a, b) => a.date.localeCompare(b.date));

          if (weekExcSlots.length === 0) return null;

          return (
            <div className="exc-summary-section" style={{ marginTop: '1rem' }}>
              <div className="exc-summary-header">
                <Sparkles size={15} className="exc-summary-icon" />
                <span className="exc-summary-title">Exceptional Slots This Week</span>
                <span className="exc-summary-count">
                  {weekExcSlots.length} date{weekExcSlots.length !== 1 ? 's' : ''}
                </span>
              </div>
              <div className="exc-summary-rows">
                {weekExcSlots.map((es) => {
                  const excDate    = new Date(es.date + 'T00:00:00');
                  const totalSlots = countWindowSlots(es.windows);
                  const bookedCount = (es.windows || []).filter(w => isWindowBooked(excDate, w)).length;
                  return (
                    <div key={es.date} className="exc-summary-row">
                      <span className="exc-row-date">{fmtExcDate(es.date)}</span>
                      <span className="exc-row-windows">
                        {(es.windows || [])
                          .slice()
                          .sort((a, b) => a.startTime.localeCompare(b.startTime))
                          .map((w, i) => {
                            const booked      = isWindowBooked(excDate, w);
                            const studentName = booked
                              ? bookedSlotNameMap.get(`${es.date}-${w.startTime}-${w.endTime}`)
                              : null;
                            return (
                              <span key={i} className={`exc-row-win ${booked ? 'exc-win-booked' : ''}`}>
                                {booked ? '✓ ' : ''}{formatTime12hr(w.startTime)}–{formatTime12hr(w.endTime)}
                                {studentName && (
                                  <span className="exc-win-student"> · {studentName}</span>
                                )}
                              </span>
                            );
                          })}
                      </span>
                      <span className="exc-row-slots">
                        {bookedCount > 0
                          ? `${bookedCount}/${(es.windows || []).length} booked`
                          : `${totalSlots} slot${totalSlots !== 1 ? 's' : ''}`
                        }
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* NO "Book Now" button for instructor view */}
      </div>
    </div>
  );
}